/**
 *  Widgets - Correct score
 *  @module Widgets
 *  @author Livescore <info@score-stats.com>
 *  @copyright 2021 livescore
 */

import { MatchService } from '@services/match.service';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BaseComponent } from '@widgets/pages/base.component';
import { Observable, Subscription, throwError } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap } from 'rxjs/operators';

import { DemoService } from '@widgets/services/demo.service';
import { WidgetMixin } from '@widgets/services/mixins/widget.mixin';

import { Form, LastForm } from '@widgets/interfaces/widget.interface';
import CONFIG from '@widgets/config/widget.config';

import { TranslateService } from '@ngx-translate/core';
import { sortBy, groupBy, each, round } from 'lodash-es';

import { MatchType } from '@/interfaces/league-table.interface';
import { ClientService } from '@/services/client.service';
import { MatchData, Team } from '@/interfaces/match-data.interface';
import { LangService } from '@/services/lang.service';

const NUM: number = 10;

@Component({
    selector: 'widgets-correct-score',
    templateUrl: './correct-score.component.html',
    styleUrls: ['../styles.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class CorrectScoreComponent extends WidgetMixin(BaseComponent) implements OnInit, OnDestroy {
    public matches: MatchData[] = [];

    private matchId: number;

    private subscriber: Subscription;

    public data: Form | null = null;

    public sort:Record<string, string | number>[] = [];
    public home:Record<string, string | number>[] = [];
    public away:Record<string, string | number>[] = [];

    public constructor(
        private router: ActivatedRoute,
        private match: MatchService,
        private lang: LangService,
        private demo: DemoService,
        public client: ClientService,
        private trans: TranslateService,
    ) {
        super(demo);
    }

    /**
     * Load data from server
     */
    public ngOnInit(): void {
        super.ngOnInit();

        this.setTitle(this.router);

        this.router.data.subscribe((sub) => {
            this.num = sub.num;
            if (sub.br && sub.br.length > 0) {
                [this.matchId] = sub.br;
            } else {
                this.finished();
            }

            if (this.matchId) {
                this.load();
            } else {
                this.error();
            }
        });
    }

    public ngOnDestroy(): void {
        if (this.subscriber instanceof Subscription) {
            this.subscriber.unsubscribe();
        }

        this.matches.splice(0);
    }

    get drawTrans(): string {
        return this.trans.instant('web.draw_match');
    }

    getSort(type: 'all' | 'home' | 'away' = 'all'): Record<string, string | number>[] {
        const home = this.data!.home.form.length > NUM ? this.data!.home.form.slice(1) : this.data!.home.form;
        const away = this.data!.away.form.length > NUM ? this.data!.away.form.slice(1) : this.data!.away.form;

        let total: number;
        let arr;
        if (type === 'all') { total = home.length + away.length; arr = home.concat(away); } else if (type === 'home') {
            total = home.length;
            arr = home;
        } else {
            total = away.length;
            arr = away;
        }
        const comb = sortBy(groupBy(arr, 'score_normal_time'), val => val.length).reverse();


        const resp = [];
        each(comb, (val) => {
            if (resp.length > 3) { return; }

            const prct = round((val.length / total) * 100, 2);
            resp.push({ score: val[0].score_normal_time, prct });
        });


        for (let x = 0; x < 3; x += 1) {
            if (!resp[x]) {
                resp[x] = { score: '-', prct: 0 };
            }
        }

        return resp;
    }

    private load(): void {
        const iso: string | null = this.lang.getLangSnapshot();
        let homeId: number | null = null;
        let awayId: number | null = null;

        this.subscriber = this.match
            .getMatchNew(this.matchId, iso as string, true)
            .pipe(
                filter((match: MatchData) => CONFIG.correctScore.availSports.includes(match.sport_code_name)),
                mergeMap(
                    (match: MatchData): Observable<Form> => {
                        const [p1, p2] = match.participants.map((p: Team): number => p.id);
                        homeId = p1;
                        awayId = p2;
                        return this.demo.form(p1, p2, iso as string, { limit: this.num });
                    },
                ),
                map((data: Form) => {
                    data.home = { ...data.home, id: homeId };
                    data.away = { ...data.away, id: awayId };

                    data.home.form = data.home.form.filter((f: LastForm) => f.type !== MatchType.UPCOMING);
                    data.away.form = data.away.form.filter((f: LastForm) => f.type !== MatchType.UPCOMING);
                    return data;
                }),
                switchMap(this.checkEmpty()),
                catchError(
                    (e): Observable<any> => {
                        this.error(e);
                        return throwError(e);
                    },
                ),
            )

            .subscribe(
                (data) => {
                    this.data = data;
                    this.sort = this.getSort();
                    this.home = this.getSort('home');
                    this.away = this.getSort('away');
                    this.finished();
                },
                () => {},
                () => {
                    this.loaded = true;
                },
            );
    }
}
